import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError, queryClient } from '@/shared/api'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { toBrowserMediaUrl } from '@/shared/lib/media-url'
import { Button, HtmlEditor, Input, Select, Textarea } from '@/shared/ui'
import {
  addAdminCollectionBundle,
  addAdminCollectionCategory,
  addAdminCollectionFilterOption,
  addAdminCollectionResource,
  addAdminCollectionVideo,
  addAdminVideoBundle,
  addAdminVideoCategory,
  addAdminVideoCollection,
  addAdminVideoFilterOption,
  addAdminVideoResource,
  createAdminCollection,
  createAdminManagedUploadUrl,
  createAdminResource,
  createAdminVideo,
  deleteAdminCollection,
  deleteAdminCollectionBundle,
  deleteAdminCollectionCategory,
  deleteAdminCollectionFilterOption,
  deleteAdminCollectionResource,
  deleteAdminCollectionVideo,
  deleteAdminManagedObject,
  deleteAdminResource,
  deleteAdminVideo,
  deleteAdminVideoBundle,
  deleteAdminVideoCategory,
  deleteAdminVideoCollection,
  deleteAdminVideoFilterOption,
  deleteAdminVideoResource,
  loadAdminCollectionDetail,
  loadAdminCollectionsList,
  loadAdminResourceDetail,
  loadAdminResourcesList,
  loadAdminVideoDetail,
  loadAdminVideosList,
  updateAdminCollection,
  updateAdminCollectionResourcePosition,
  updateAdminCollectionVideoLink,
  updateAdminResource,
  updateAdminVideo,
  updateAdminVideoCollectionLink,
  updateAdminVideoResourcePosition,
  type CollectionDetail,
  type ManagedMediaKind,
  type ResourceDetail,
  type VideoDetail,
  type VideotecaAccess,
} from '../api/video-library-admin-api'
import {
  formatCatalogCoverValidationHint,
  validateCatalogCoverImage,
} from '../lib/catalog-cover-image'
import { ReferenceBrowser } from './reference-browser'

const pageSize = 12
const storageHttpBase = 'https://storage.yandexcloud.net'

const sectionHeadingStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const bodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const smallBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const mutedStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const labelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const panelStyle = {
  borderColor: designTokens.colors.border.adminPattern,
  backgroundColor: designTokens.colors.background.surface,
}

type BannerTone = 'error' | 'success'

type FieldProps = {
  label: string
  children: ReactNode
  className?: string
}

type UploadedManagedFile = {
  bucket: string
  key: string
  browserUrl: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))

const formatFileSize = (value: number | null) => {
  if (!value) {
    return '0 B'
  }

  if (value < 1024) {
    return `${value} B`
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`
  }

  if (value < 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`
  }

  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const formatDuration = (seconds: number | null) => {
  if (!seconds) {
    return '0:00'
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

const formatError = (error: unknown) => {
  if (error instanceof ApiError) {
    return `${error.status} ${error.statusText}`
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unexpected admin error'
}

const successToneFromMessage = (message: string): BannerTone =>
  message.includes('создан') ||
  message.includes('создана') ||
  message.includes('сохран') ||
  message.includes('удал') ||
  message.includes('загруж')
    ? 'success'
    : 'error'

const resolveSelectedId = <T extends { id: string }>(
  rows: T[] | undefined,
  requestedId: string | null
) => {
  const items = rows ?? []
  if (items.length === 0) {
    return null
  }

  if (requestedId && items.some((item) => item.id === requestedId)) {
    return requestedId
  }

  return items[0]?.id ?? null
}

const toStorageUrl = (bucket: string, key: string) =>
  `${storageHttpBase}/${encodeURIComponent(bucket)}/${key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')}`

const getVisibilityState = (
  visibleFromAt: string | null
): 'unpublished' | 'published' | 'scheduled' => {
  if (!visibleFromAt) {
    return 'unpublished'
  }

  const date = new Date(visibleFromAt)
  if (date.getTime() <= Date.now()) {
    return 'published'
  }

  return 'scheduled'
}

const toLocalDateTimeInputValue = (value: string | null) => {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  const pad = (input: number) => String(input).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const toVisibilityPayload = (
  state: 'unpublished' | 'published' | 'scheduled',
  currentVisibleFromAt: string | null,
  scheduledLocalValue: string
) => {
  if (state === 'unpublished') {
    return null
  }

  if (state === 'published') {
    if (currentVisibleFromAt) {
      const current = new Date(currentVisibleFromAt)
      if (current.getTime() <= Date.now()) {
        return current.toISOString()
      }
    }

    return new Date().toISOString()
  }

  if (!scheduledLocalValue) {
    return currentVisibleFromAt ?? null
  }

  return new Date(scheduledLocalValue).toISOString()
}

async function uploadManagedFile(input: {
  kind: ManagedMediaKind
  file: File
  entityId: string
}): Promise<UploadedManagedFile> {
  const response = await createAdminManagedUploadUrl({
    kind: input.kind,
    fileName: input.file.name,
    contentType: input.file.type || 'application/octet-stream',
    entityId: input.entityId,
  })

  const uploadResponse = await fetch(response.upload.uploadUrl, {
    method: response.upload.uploadMethod,
    headers: response.upload.requiredHeaders,
    body: input.file,
  })

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`)
  }

  return {
    bucket: response.upload.bucket,
    key: response.upload.key,
    browserUrl: toStorageUrl(response.upload.bucket, response.upload.key),
    fileName: input.file.name,
    mimeType: input.file.type || 'application/octet-stream',
    sizeBytes: input.file.size,
  }
}

async function deleteManagedFileIfPresent(key: string | null | undefined, kind: ManagedMediaKind) {
  if (!key) {
    return
  }

  await deleteAdminManagedObject({ key, kind })
}

function FeedbackBanner({ message, tone = 'error' }: { message: string; tone?: BannerTone }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        ...smallBodyStyle,
        backgroundColor: tone === 'success' ? '#dcfce7' : '#fee2e2',
        color: tone === 'success' ? '#166534' : '#991b1b',
      }}
    >
      {message}
    </div>
  )
}

function PanelSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-[24px] border p-4" style={panelStyle}>
      <div className="mb-4 flex flex-col gap-1">
        <h3 style={sectionHeadingStyle}>{title}</h3>
        {description ? <p style={mutedStyle}>{description}</p> : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  )
}

function Field({ label, children, className }: FieldProps) {
  return (
    <div className={className ? `flex flex-col gap-2 ${className}` : 'flex flex-col gap-2'}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  )
}

function PaginationControls({
  page,
  hasMore,
  total,
  limit,
  onPrevious,
  onNext,
  onSelectPage,
}: {
  page: number
  hasMore: boolean
  total: number
  limit: number
  onPrevious: () => void
  onNext: () => void
  onSelectPage: (page: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const pageNumbers = new Set<number>([1, totalPages, page - 1, page, page + 1, page + 2])
  const visiblePages = Array.from(pageNumbers)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((left, right) => left - right)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span style={mutedStyle}>Всего: {total}</span>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondaryAssist" disabled={page <= 1} onClick={onPrevious}>
          Назад
        </Button>
        {visiblePages.map((item, index) => {
          const previousPage = visiblePages[index - 1]
          const showEllipsis = previousPage !== undefined && item - previousPage > 1

          return (
            <div key={item} className="flex items-center gap-2">
              {showEllipsis ? <span style={mutedStyle}>…</span> : null}
              <Button
                type="button"
                variant={item === page ? 'primary' : 'secondaryAssist'}
                onClick={() => onSelectPage(item)}
              >
                {item}
              </Button>
            </div>
          )
        })}
        <Button type="button" variant="secondaryAssist" disabled={!hasMore} onClick={onNext}>
          Вперёд
        </Button>
      </div>
    </div>
  )
}

function ListSelectionButton({
  active,
  onClick,
  coverUrl,
  coverAlt,
  children,
}: {
  active: boolean
  onClick: () => void
  coverUrl?: string | null
  coverAlt?: string
  children: ReactNode
}) {
  const normalizedCoverUrl = toBrowserMediaUrl(coverUrl)

  return (
    <button
      type="button"
      aria-pressed={active}
      className="rounded-2xl border px-4 py-4 text-left transition"
      style={{
        ...panelStyle,
        borderColor: active
          ? designTokens.colors.border.inputFocus
          : designTokens.colors.border.adminPattern,
        backgroundColor: active ? '#f8f4ff' : designTokens.colors.background.surface,
        boxShadow: active
          ? '0 0 0 2px rgba(118, 92, 255, 0.12), 0 18px 36px rgba(15, 23, 42, 0.08)'
          : '0 8px 24px rgba(15, 23, 42, 0.04)',
      }}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        <div
          className="shrink-0 rounded-full"
          style={{
            width: active ? '10px' : '8px',
            minHeight: active ? '56px' : '48px',
            backgroundColor: active ? designTokens.colors.border.inputFocus : '#ddd3eb',
          }}
        />
        {normalizedCoverUrl ? (
          <img
            src={normalizedCoverUrl}
            alt={coverAlt ?? 'Cover preview'}
            className="h-16 w-24 shrink-0 rounded-2xl object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </button>
  )
}

function KeywordsEditor({
  keywords,
  onChange,
}: {
  keywords: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const addKeyword = () => {
    const normalized = draft.trim()
    if (!normalized || keywords.includes(normalized)) {
      return
    }

    onChange([...keywords, normalized])
    setDraft('')
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border p-4" style={panelStyle}>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Добавить keyword"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addKeyword()
            }
          }}
        />
        <Button type="button" variant="secondaryAssist" onClick={addKeyword}>
          Добавить
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {keywords.map((keyword) => (
          <button
            key={keyword}
            type="button"
            className="rounded-full border px-3 py-1"
            style={panelStyle}
            onClick={() => onChange(keywords.filter((item) => item !== keyword))}
          >
            <span style={smallBodyStyle}>{keyword} ×</span>
          </button>
        ))}
        {keywords.length === 0 ? <span style={mutedStyle}>Пока нет тегов.</span> : null}
      </div>
    </div>
  )
}

function VisibilityEditor({
  currentVisibleFromAt,
  visibilityState,
  setVisibilityState,
  scheduledValue,
  setScheduledValue,
}: {
  currentVisibleFromAt: string | null
  visibilityState: 'unpublished' | 'published' | 'scheduled'
  setVisibilityState: (next: 'unpublished' | 'published' | 'scheduled') => void
  scheduledValue: string
  setScheduledValue: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border p-4" style={panelStyle}>
      <Select
        value={visibilityState}
        onChange={(event) =>
          setVisibilityState(event.target.value as 'unpublished' | 'published' | 'scheduled')
        }
      >
        <option value="unpublished">Unpublished</option>
        <option value="published">Published</option>
        <option value="scheduled">Scheduled</option>
      </Select>
      {visibilityState === 'scheduled' ? (
        <Input
          type="datetime-local"
          value={scheduledValue}
          onChange={(event) => setScheduledValue(event.target.value)}
        />
      ) : null}
      <span style={mutedStyle}>
        Сейчас: {currentVisibleFromAt ? formatDateTime(currentVisibleFromAt) : 'Не опубликовано'}
      </span>
    </div>
  )
}

function ManagedFileField({
  label,
  fileLabel,
  accept,
  hint,
  showInlineFeedback = false,
  uploadSuccessMessage = 'Файл загружен',
  removeSuccessMessage = 'Файл удалён',
  onUpload,
  onRemove,
}: {
  label: string
  fileLabel: string | null
  accept?: string
  hint?: string
  showInlineFeedback?: boolean
  uploadSuccessMessage?: string
  removeSuccessMessage?: string
  onUpload: (file: File) => Promise<void>
  onRemove: () => Promise<void>
}) {
  const [inlineFeedback, setInlineFeedback] = useState<string | null>(null)

  const uploadFile = async (file: File) => {
    if (showInlineFeedback) {
      setInlineFeedback(null)
    }

    try {
      await onUpload(file)

      if (showInlineFeedback) {
        setInlineFeedback(uploadSuccessMessage)
      }
    } catch (error) {
      if (showInlineFeedback) {
        setInlineFeedback(formatError(error))
        return
      }

      throw error
    }
  }

  const removeFile = async () => {
    if (showInlineFeedback) {
      setInlineFeedback(null)
    }

    try {
      await onRemove()

      if (showInlineFeedback) {
        setInlineFeedback(removeSuccessMessage)
      }
    } catch (error) {
      if (showInlineFeedback) {
        setInlineFeedback(formatError(error))
        return
      }

      throw error
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border p-4" style={panelStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={fileLabel ? bodyStyle : mutedStyle}>
        {fileLabel ? `Текущий файл: ${fileLabel}` : 'Файл не привязан'}
      </span>
      <input
        type="file"
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) {
            void uploadFile(file)
          }
          event.currentTarget.value = ''
        }}
      />
      <span style={mutedStyle}>
        После выбора новый файл загружается сразу. Нативное поле затем очищается автоматически.
      </span>
      {hint ? <span style={mutedStyle}>{hint}</span> : null}
      {inlineFeedback ? (
        <FeedbackBanner message={inlineFeedback} tone={successToneFromMessage(inlineFeedback)} />
      ) : null}
      {fileLabel ? (
        <div className="flex justify-end">
          <Button type="button" variant="secondaryAssist" onClick={() => void removeFile()}>
            Удалить файл
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function FilterGroupsEditor({
  groups,
  onSelectOption,
  onClearSelection,
}: {
  groups: Array<{
    filterId: string
    filterTitle: string
    filterPosition: number
    selectedLinkId: string | null
    selectedOptionId: string | null
    options: Array<{
      optionId: string
      optionTitle: string
      optionPosition: number
    }>
  }>
  onSelectOption: (optionId: string) => Promise<void>
  onClearSelection: (linkId: string) => Promise<void>
}) {
  const [busyFilterId, setBusyFilterId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)

  const handleChange = async (
    filterId: string,
    nextOptionId: string,
    selectedLinkId: string | null
  ) => {
    setBusyFilterId(filterId)
    setFeedback(null)

    try {
      if (!nextOptionId) {
        if (selectedLinkId) {
          await onClearSelection(selectedLinkId)
          setFeedback('Фильтр очищен')
        }
        return
      }

      await onSelectOption(nextOptionId)
      setFeedback('Фильтр сохранён')
    } catch (error) {
      setFeedback(formatError(error))
    } finally {
      setBusyFilterId(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <div
          key={group.filterId}
          className="grid gap-3 rounded-2xl border p-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-center"
          style={panelStyle}
        >
          <div className="flex flex-col gap-1">
            <strong style={smallBodyStyle}>{group.filterTitle}</strong>
            <span style={mutedStyle}>
              options: {group.options.length}
              {group.selectedOptionId ? ' · выбрано' : ''}
            </span>
          </div>
          <Select
            value={group.selectedOptionId ?? ''}
            disabled={busyFilterId === group.filterId}
            onChange={(event) =>
              void handleChange(group.filterId, event.target.value, group.selectedLinkId)
            }
          >
            <option value="">Очистить значение</option>
            {group.options.map((option) => (
              <option key={option.optionId} value={option.optionId}>
                {option.optionTitle}
              </option>
            ))}
          </Select>
        </div>
      ))}

      {groups.length === 0 ? <p style={mutedStyle}>Группы custom filters ещё не созданы.</p> : null}

      {feedback ? (
        <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
      ) : null}
    </div>
  )
}

function ResourcesEditor({
  detail,
  onSave,
  onDelete,
  onUploadFile,
  onRemoveFile,
}: {
  detail: ResourceDetail
  onSave: (input: {
    title: string
    descriptionHtml: string | null
    fileName: string
    storageBucket: string | null
    storageKey: string | null
    fileUrl: string | null
    mimeType: string | null
    sizeBytes: number | null
  }) => Promise<void>
  onDelete: () => Promise<void>
  onUploadFile: (file: File) => Promise<UploadedManagedFile>
  onRemoveFile: () => Promise<void>
}) {
  const [title, setTitle] = useState(detail.resource.title)
  const [descriptionHtml, setDescriptionHtml] = useState(detail.resource.descriptionHtml ?? '')
  const [fileName, setFileName] = useState(detail.resource.fileName)
  const [storageBucket, setStorageBucket] = useState(detail.resource.storageBucket ?? '')
  const [storageKey, setStorageKey] = useState(detail.resource.storageKey ?? '')
  const [fileUrl, setFileUrl] = useState(detail.resource.fileUrl ?? '')
  const [mimeType, setMimeType] = useState(detail.resource.mimeType ?? '')
  const [sizeBytes, setSizeBytes] = useState(String(detail.resource.sizeBytes ?? ''))

  const handleUploadFile = async (file: File) => {
    const upload = await onUploadFile(file)
    setFileName(upload.fileName)
    setStorageBucket(upload.bucket)
    setStorageKey(upload.key)
    setFileUrl(upload.browserUrl)
    setMimeType(upload.mimeType)
    setSizeBytes(String(upload.sizeBytes))
  }

  const handleRemoveFile = async () => {
    await onRemoveFile()
    setFileName(`${title.trim() || detail.resource.title}.bin`)
    setStorageBucket('')
    setStorageKey('')
    setFileUrl('')
    setMimeType('')
    setSizeBytes('')
  }

  return (
    <>
      <PanelSection title="Параметры ресурса">
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="File name">
            <Input value={fileName} onChange={(event) => setFileName(event.target.value)} />
          </Field>
          <Field label="Description" className="lg:col-span-2">
            <HtmlEditor value={descriptionHtml} onChange={setDescriptionHtml} />
          </Field>
          <Field label="Storage bucket">
            <Input
              value={storageBucket}
              onChange={(event) => setStorageBucket(event.target.value)}
            />
          </Field>
          <Field label="Storage key">
            <Input value={storageKey} onChange={(event) => setStorageKey(event.target.value)} />
          </Field>
          <Field label="File URL">
            <Input value={fileUrl} onChange={(event) => setFileUrl(event.target.value)} />
          </Field>
          <Field label="Mime type">
            <Input value={mimeType} onChange={(event) => setMimeType(event.target.value)} />
          </Field>
          <Field label="Size bytes">
            <Input
              type="number"
              value={sizeBytes}
              onChange={(event) => setSizeBytes(event.target.value)}
            />
          </Field>
          <Field label="Updated">
            <p style={bodyStyle}>{formatDateTime(detail.resource.updatedAt)}</p>
          </Field>
        </div>

        <ManagedFileField
          label="Файл ресурса"
          fileLabel={storageKey || fileUrl || fileName}
          onUpload={handleUploadFile}
          onRemove={handleRemoveFile}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondaryAssist" onClick={() => void onDelete()}>
            Удалить ресурс
          </Button>
          <Button
            type="button"
            onClick={() =>
              void onSave({
                title: title.trim(),
                descriptionHtml: descriptionHtml.trim() || null,
                fileName: fileName.trim(),
                storageBucket: storageBucket.trim() || null,
                storageKey: storageKey.trim() || null,
                fileUrl: fileUrl.trim() || null,
                mimeType: mimeType.trim() || null,
                sizeBytes: sizeBytes.trim() ? Number(sizeBytes) : null,
              })
            }
          >
            Сохранить ресурс
          </Button>
        </div>
      </PanelSection>

      <PanelSection title="Использование ресурса">
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-2xl border p-4" style={panelStyle}>
            <strong style={smallBodyStyle}>Collections</strong>
            <div className="mt-3 flex flex-col gap-2">
              {detail.collections.map((item) => (
                <span key={item.linkId} style={mutedStyle}>
                  {item.title} · position: {item.position}
                </span>
              ))}
              {detail.collections.length === 0 ? <span style={mutedStyle}>Нет связей</span> : null}
            </div>
          </div>
          <div className="rounded-2xl border p-4" style={panelStyle}>
            <strong style={smallBodyStyle}>Videos</strong>
            <div className="mt-3 flex flex-col gap-2">
              {detail.videos.map((item) => (
                <span key={item.linkId} style={mutedStyle}>
                  {item.title} · position: {item.position}
                </span>
              ))}
              {detail.videos.length === 0 ? <span style={mutedStyle}>Нет связей</span> : null}
            </div>
          </div>
        </div>
      </PanelSection>
    </>
  )
}

export function ResourcesTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'resources', page, appliedSearch],
    queryFn: () => loadAdminResourcesList({ page, limit: pageSize, search: appliedSearch }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)

  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'resource-detail', selectedId],
    queryFn: () => loadAdminResourceDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'resources'] })
    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'resource-detail', selectedId],
      })
    }
  }

  const createResourceHandler = async () => {
    if (!createTitle.trim()) {
      return
    }

    try {
      const response = await createAdminResource({
        title: createTitle.trim(),
        fileName: `${createTitle.trim()}.bin`,
      })
      setCreateTitle('')
      setRequestedId(response.detail.resource.id)
      setFeedback('Ресурс создан')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveResource = async (input: Parameters<typeof updateAdminResource>[1]) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminResource(selectedId, input)
      setFeedback('Ресурс сохранён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const uploadFile = async (file: File) => {
    if (!selectedId || !detailQuery.data) {
      throw new Error('Resource is not selected')
    }

    let upload: UploadedManagedFile | null = null

    try {
      const previousKey = detailQuery.data.resource.storageKey
      upload = await uploadManagedFile({
        kind: 'resource',
        file,
        entityId: selectedId,
      })

      await updateAdminResource(selectedId, {
        fileName: upload.fileName,
        storageBucket: upload.bucket,
        storageKey: upload.key,
        fileUrl: upload.browserUrl,
        mimeType: upload.mimeType,
        sizeBytes: upload.sizeBytes,
      })
      if (previousKey && previousKey !== upload.key) {
        await deleteManagedFileIfPresent(previousKey, 'resource')
      }

      setFeedback('Файл ресурса загружен')
      await refresh()
      return upload
    } catch (error) {
      if (upload?.key) {
        await deleteManagedFileIfPresent(upload.key, 'resource').catch(() => undefined)
      }
      setFeedback(formatError(error))
      throw error
    }
  }

  const removeFile = async () => {
    if (!selectedId || !detailQuery.data) {
      return
    }

    if (!window.confirm('Удалить файл ресурса?')) {
      return
    }

    const currentKey = detailQuery.data.resource.storageKey

    try {
      await updateAdminResource(selectedId, {
        fileName: `${detailQuery.data.resource.title}.bin`,
        storageBucket: null,
        storageKey: null,
        fileUrl: null,
        mimeType: null,
        sizeBytes: null,
      })
      await deleteManagedFileIfPresent(currentKey, 'resource')
      setFeedback('Файл ресурса удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const deleteResourceHandler = async () => {
    if (!selectedId || !window.confirm('Удалить ресурс?')) {
      return
    }

    try {
      await deleteAdminResource(selectedId)
      setRequestedId(null)
      setFeedback('Ресурс удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection
        title="Ресурсы"
        description="Файлы, которые могут быть привязаны к collection и video через link-таблицы."
      >
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title / file name"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новый ресурс</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Resource title"
          />
          <Button type="button" onClick={() => void createResourceHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>{item.fileName}</span>
              <span style={mutedStyle}>
                collections: {item.collectionCount} · videos: {item.videoCount} ·{' '}
                {formatFileSize(item.sizeBytes)}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}
        {detailQuery.data ? (
          <ResourcesEditor
            key={detailQuery.data.resource.id}
            detail={detailQuery.data}
            onSave={saveResource}
            onDelete={deleteResourceHandler}
            onUploadFile={uploadFile}
            onRemoveFile={removeFile}
          />
        ) : (
          <PanelSection title="Параметры ресурса">
            <p style={mutedStyle}>Выберите ресурс слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

function CollectionEditor({
  detail,
  onSave,
  onDelete,
  onUploadCover,
  onRemoveCover,
  onAddBundle,
  onRemoveBundle,
  onAddCategory,
  onRemoveCategory,
  onAddFilterOption,
  onRemoveFilterOption,
  onAddResource,
  onMoveResource,
  onRemoveResource,
  onAddVideo,
  onMoveVideo,
  onToggleVideoFree,
  onRemoveVideo,
}: {
  detail: CollectionDetail
  onSave: (input: {
    title: string
    descriptionHtml: string | null
    shortDescription: string | null
    visibleFromAt: string | null
    access: VideotecaAccess
    urlSlug: string
    searchKeywords: string[]
  }) => Promise<void>
  onDelete: () => Promise<void>
  onUploadCover: (file: File) => Promise<UploadedManagedFile>
  onRemoveCover: () => Promise<void>
  onAddBundle: (bundleId: string) => Promise<void>
  onRemoveBundle: (linkId: string) => Promise<void>
  onAddCategory: (categoryId: string) => Promise<void>
  onRemoveCategory: (linkId: string) => Promise<void>
  onAddFilterOption: (optionId: string) => Promise<void>
  onRemoveFilterOption: (linkId: string) => Promise<void>
  onAddResource: (resourceId: string) => Promise<void>
  onMoveResource: (linkId: string, position: number) => Promise<void>
  onRemoveResource: (linkId: string) => Promise<void>
  onAddVideo: (videoId: string) => Promise<void>
  onMoveVideo: (linkId: string, position: number) => Promise<void>
  onToggleVideoFree: (linkId: string, isFree: boolean, position: number) => Promise<void>
  onRemoveVideo: (linkId: string) => Promise<void>
}) {
  const normalizedCollectionCoverUrl = toBrowserMediaUrl(detail.collection.coverUrl)
  const [title, setTitle] = useState(detail.collection.title)
  const [descriptionHtml, setDescriptionHtml] = useState(detail.collection.descriptionHtml ?? '')
  const [shortDescription, setShortDescription] = useState(detail.collection.shortDescription ?? '')
  const [access, setAccess] = useState<VideotecaAccess>(detail.collection.access)
  const [urlSlug, setUrlSlug] = useState(detail.collection.urlSlug)
  const [keywords, setKeywords] = useState(detail.collection.searchKeywords)
  const [visibilityState, setVisibilityState] = useState<'unpublished' | 'published' | 'scheduled'>(
    getVisibilityState(detail.collection.visibleFromAt)
  )
  const [scheduledValue, setScheduledValue] = useState(
    toLocalDateTimeInputValue(detail.collection.visibleFromAt)
  )

  const handleUploadCover = async (file: File) => {
    await onUploadCover(file)
  }

  const handleRemoveCover = async () => {
    await onRemoveCover()
  }

  return (
    <>
      <PanelSection title="Параметры коллекции">
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="URL slug">
            <Input value={urlSlug} onChange={(event) => setUrlSlug(event.target.value)} />
          </Field>
          <Field label="Description" className="lg:col-span-2">
            <HtmlEditor value={descriptionHtml} onChange={setDescriptionHtml} />
          </Field>
          <Field label="Short description" className="lg:col-span-2">
            <Textarea
              value={shortDescription}
              onChange={(event) => setShortDescription(event.target.value)}
            />
          </Field>
          <Field label="Access">
            <Select
              value={access}
              onChange={(event) => setAccess(event.target.value as VideotecaAccess)}
            >
              <option value="gated">gated</option>
              <option value="free_for_all">free_for_all</option>
            </Select>
          </Field>
          <Field label="Visibility">
            <VisibilityEditor
              currentVisibleFromAt={detail.collection.visibleFromAt}
              visibilityState={visibilityState}
              setVisibilityState={setVisibilityState}
              scheduledValue={scheduledValue}
              setScheduledValue={setScheduledValue}
            />
          </Field>
          <Field label="Stats">
            <p style={bodyStyle}>
              videos: {detail.collection.totalVideos ?? 0} · duration:{' '}
              {formatDuration(detail.collection.totalDurationSeconds)}
            </p>
          </Field>
          <Field label="Updated">
            <p style={bodyStyle}>{formatDateTime(detail.collection.updatedAt)}</p>
          </Field>
        </div>

        <Field label="Search keywords">
          <KeywordsEditor keywords={keywords} onChange={setKeywords} />
        </Field>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondaryAssist" onClick={() => void onDelete()}>
            Удалить коллекцию
          </Button>
          <Button
            type="button"
            onClick={() =>
              void onSave({
                title: title.trim(),
                descriptionHtml: descriptionHtml.trim() || null,
                shortDescription: shortDescription.trim() || null,
                visibleFromAt: toVisibilityPayload(
                  visibilityState,
                  detail.collection.visibleFromAt,
                  scheduledValue
                ),
                access,
                urlSlug: urlSlug.trim(),
                searchKeywords: keywords,
              })
            }
          >
            Сохранить коллекцию
          </Button>
        </div>
      </PanelSection>

      <PanelSection
        title="Cover"
        description="Обложка коллекции сохраняется и заменяется сразу, отдельно от основной кнопки сохранения."
      >
        <ManagedFileField
          label="Cover"
          fileLabel={detail.collection.coverKey || detail.collection.coverUrl}
          accept="image/jpeg,image/png,image/webp"
          hint={formatCatalogCoverValidationHint()}
          showInlineFeedback
          uploadSuccessMessage="Обложка коллекции загружена"
          removeSuccessMessage="Обложка коллекции удалена"
          onUpload={handleUploadCover}
          onRemove={handleRemoveCover}
        />
        {normalizedCollectionCoverUrl ? (
          <img
            src={normalizedCollectionCoverUrl}
            alt={detail.collection.title}
            className="max-h-56 rounded-2xl object-cover"
          />
        ) : null}
      </PanelSection>

      <PanelSection title="Bundles">
        {detail.bundles.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>position: {item.position}</span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveBundle(item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        <ReferenceBrowser
          kind="bundle"
          actionLabel="Добавить bundle"
          linkedItems={detail.bundles.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddBundle}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveBundle(linkId) : Promise.resolve())}
        />
      </PanelSection>

      <PanelSection title="Категории">
        {detail.categories.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.urlSlug} · position: {item.position}
              </span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveCategory(item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        <ReferenceBrowser
          kind="category"
          actionLabel="Добавить категорию"
          linkedItems={detail.categories.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddCategory}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveCategory(linkId) : Promise.resolve())}
        />
      </PanelSection>

      <PanelSection title="Custom filters">
        <FilterGroupsEditor
          groups={detail.filterGroups}
          onSelectOption={onAddFilterOption}
          onClearSelection={onRemoveFilterOption}
        />
      </PanelSection>

      <PanelSection title="Resources">
        {detail.resources.map((item, index) => (
          <div
            key={item.linkId}
            className="flex flex-col gap-3 rounded-2xl border p-4 lg:flex-row lg:items-center lg:justify-between"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.fileName} · {item.mimeType ?? 'mime?'} · position: {item.position}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() => void onMoveResource(item.linkId, item.position - 1)}
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.resources.length - 1}
                onClick={() => void onMoveResource(item.linkId, item.position + 1)}
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onRemoveResource(item.linkId)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}
        <ReferenceBrowser
          kind="resource"
          actionLabel="Добавить resource"
          linkedItems={detail.resources.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddResource}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveResource(linkId) : Promise.resolve())}
          searchPlaceholder="Поиск по title"
        />
      </PanelSection>

      <PanelSection title="Videos">
        {detail.videos.map((item, index) => (
          <div
            key={item.linkId}
            className="flex flex-col gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.urlSlug} · {formatDuration(item.durationSeconds)} · position: {item.position}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() => void onMoveVideo(item.linkId, item.position - 1)}
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.videos.length - 1}
                onClick={() => void onMoveVideo(item.linkId, item.position + 1)}
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onToggleVideoFree(item.linkId, !item.isFree, item.position)}
              >
                {item.isFree ? 'Сделать gated' : 'Сделать free'}
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onRemoveVideo(item.linkId)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}
        <ReferenceBrowser
          kind="video"
          actionLabel="Добавить video"
          linkedItems={detail.videos.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddVideo}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveVideo(linkId) : Promise.resolve())}
        />
      </PanelSection>
    </>
  )
}

export function CollectionsTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'collections', page, appliedSearch],
    queryFn: () => loadAdminCollectionsList({ page, limit: pageSize, search: appliedSearch }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)
  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'collection-detail', selectedId],
    queryFn: () => loadAdminCollectionDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'collections'] })
    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'collection-detail', selectedId],
      })
    }
  }

  const createCollectionHandler = async () => {
    if (!createTitle.trim() || !createSlug.trim()) {
      return
    }

    try {
      const response = await createAdminCollection({
        title: createTitle.trim(),
        urlSlug: createSlug.trim(),
      })
      setCreateTitle('')
      setCreateSlug('')
      setRequestedId(response.detail.collection.id)
      setFeedback('Коллекция создана')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveCollection = async (input: Parameters<typeof updateAdminCollection>[1]) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminCollection(selectedId, input)
      setFeedback('Коллекция сохранена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const uploadCover = async (file: File) => {
    if (!selectedId) {
      throw new Error('Collection is not selected')
    }

    let upload: UploadedManagedFile | null = null

    try {
      await validateCatalogCoverImage(file)
      const previousKey = detailQuery.data?.collection.coverKey
      upload = await uploadManagedFile({
        kind: 'collectionCover',
        file,
        entityId: selectedId,
      })
      await updateAdminCollection(selectedId, {
        coverBucket: upload.bucket,
        coverKey: upload.key,
        coverUrl: upload.browserUrl,
      })
      if (previousKey && previousKey !== upload.key) {
        await deleteManagedFileIfPresent(previousKey, 'collectionCover')
      }
      await refresh()
      return upload
    } catch (error) {
      if (upload?.key) {
        await deleteManagedFileIfPresent(upload.key, 'collectionCover').catch(() => undefined)
      }
      throw error
    }
  }

  const removeCover = async () => {
    if (!selectedId || !detailQuery.data) {
      return
    }

    if (!window.confirm('Удалить обложку коллекции?')) {
      return
    }

    const oldKey = detailQuery.data.collection.coverKey

    await updateAdminCollection(selectedId, {
      coverBucket: null,
      coverKey: null,
      coverUrl: null,
    })
    await deleteManagedFileIfPresent(oldKey, 'collectionCover')
    await refresh()
  }

  const deleteCollectionHandler = async () => {
    if (!selectedId || !window.confirm('Удалить коллекцию?')) {
      return
    }

    try {
      await deleteAdminCollection(selectedId)
      setRequestedId(null)
      setFeedback('Коллекция удалена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const simpleAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action()
      setFeedback(successMessage)
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection
        title="Collections"
        description="Основная форма контента, связки и ordered video/resource lists."
      >
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title / slug"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новая коллекция</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Title"
          />
          <Input
            value={createSlug}
            onChange={(event) => setCreateSlug(event.target.value)}
            placeholder="URL slug"
          />
          <Button type="button" onClick={() => void createCollectionHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
            coverUrl={item.coverUrl}
            coverAlt={item.title}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>{item.urlSlug}</span>
              <span style={mutedStyle}>
                videos: {item.videoCount} · resources: {item.resourceCount} · bundles:{' '}
                {item.bundleCount}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}
        {detailQuery.data ? (
          <CollectionEditor
            key={detailQuery.data.collection.id}
            detail={detailQuery.data}
            onSave={saveCollection}
            onDelete={deleteCollectionHandler}
            onUploadCover={uploadCover}
            onRemoveCover={removeCover}
            onAddBundle={(bundleId) =>
              simpleAction(
                () => addAdminCollectionBundle({ id: selectedId as string, bundleId }),
                'Связь с bundle сохранена'
              )
            }
            onRemoveBundle={(linkId) =>
              window.confirm('Удалить связь с bundle?')
                ? simpleAction(
                    () => deleteAdminCollectionBundle({ id: selectedId as string, linkId }),
                    'Связь с bundle удалена'
                  )
                : Promise.resolve()
            }
            onAddCategory={(categoryId) =>
              simpleAction(
                () => addAdminCollectionCategory({ id: selectedId as string, categoryId }),
                'Категория добавлена'
              )
            }
            onRemoveCategory={(linkId) =>
              window.confirm('Удалить связь с категорией?')
                ? simpleAction(
                    () => deleteAdminCollectionCategory({ id: selectedId as string, linkId }),
                    'Категория удалена'
                  )
                : Promise.resolve()
            }
            onAddFilterOption={(optionId) =>
              simpleAction(
                () => addAdminCollectionFilterOption({ id: selectedId as string, optionId }),
                'Фильтр сохранён'
              )
            }
            onRemoveFilterOption={(linkId) =>
              window.confirm('Удалить выбранную filter option?')
                ? simpleAction(
                    () => deleteAdminCollectionFilterOption({ id: selectedId as string, linkId }),
                    'Фильтр удалён'
                  )
                : Promise.resolve()
            }
            onAddResource={(resourceId) =>
              simpleAction(
                () => addAdminCollectionResource({ id: selectedId as string, resourceId }),
                'Ресурс добавлен'
              )
            }
            onMoveResource={(linkId, position) =>
              simpleAction(
                () =>
                  updateAdminCollectionResourcePosition({
                    id: selectedId as string,
                    linkId,
                    position,
                  }),
                'Порядок ресурсов обновлён'
              )
            }
            onRemoveResource={(linkId) =>
              window.confirm('Удалить ресурс из коллекции?')
                ? simpleAction(
                    () => deleteAdminCollectionResource({ id: selectedId as string, linkId }),
                    'Ресурс удалён из коллекции'
                  )
                : Promise.resolve()
            }
            onAddVideo={(videoId) =>
              simpleAction(
                () => addAdminCollectionVideo({ id: selectedId as string, videoId }),
                'Видео добавлено'
              )
            }
            onMoveVideo={(linkId, position) =>
              simpleAction(
                () =>
                  updateAdminCollectionVideoLink({ id: selectedId as string, linkId, position }),
                'Порядок видео обновлён'
              )
            }
            onToggleVideoFree={(linkId, isFree, position) =>
              simpleAction(
                () =>
                  updateAdminCollectionVideoLink({
                    id: selectedId as string,
                    linkId,
                    isFree,
                    position,
                  }),
                'Флаг free обновлён'
              )
            }
            onRemoveVideo={(linkId) =>
              window.confirm('Удалить видео из коллекции?')
                ? simpleAction(
                    () => deleteAdminCollectionVideo({ id: selectedId as string, linkId }),
                    'Видео удалено из коллекции'
                  )
                : Promise.resolve()
            }
          />
        ) : (
          <PanelSection title="Параметры коллекции">
            <p style={mutedStyle}>Выберите коллекцию слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

function VideoEditor({
  detail,
  onSave,
  onDelete,
  onUploadCover,
  onRemoveCover,
  onUploadVideo,
  onRemoveVideoFile,
  onAddBundle,
  onRemoveBundle,
  onAddCategory,
  onRemoveCategory,
  onAddFilterOption,
  onRemoveFilterOption,
  onAddResource,
  onMoveResource,
  onRemoveResource,
  onAddCollection,
  onMoveCollection,
  onToggleCollectionFree,
  onRemoveCollection,
}: {
  detail: VideoDetail
  onSave: (input: {
    title: string
    descriptionHtml: string | null
    shortDescription: string | null
    visibleFromAt: string | null
    access: VideotecaAccess
    durationSeconds: number | null
    urlSlug: string
    searchKeywords: string[]
  }) => Promise<void>
  onDelete: () => Promise<void>
  onUploadCover: (file: File) => Promise<UploadedManagedFile>
  onRemoveCover: () => Promise<void>
  onUploadVideo: (file: File) => Promise<UploadedManagedFile>
  onRemoveVideoFile: () => Promise<void>
  onAddBundle: (bundleId: string) => Promise<void>
  onRemoveBundle: (linkId: string) => Promise<void>
  onAddCategory: (categoryId: string) => Promise<void>
  onRemoveCategory: (linkId: string) => Promise<void>
  onAddFilterOption: (optionId: string) => Promise<void>
  onRemoveFilterOption: (linkId: string) => Promise<void>
  onAddResource: (resourceId: string) => Promise<void>
  onMoveResource: (linkId: string, position: number) => Promise<void>
  onRemoveResource: (linkId: string) => Promise<void>
  onAddCollection: (collectionId: string) => Promise<void>
  onMoveCollection: (linkId: string, position: number) => Promise<void>
  onToggleCollectionFree: (linkId: string, isFree: boolean, position: number) => Promise<void>
  onRemoveCollection: (linkId: string) => Promise<void>
}) {
  const normalizedVideoCoverUrl = toBrowserMediaUrl(detail.video.coverUrl)
  const [title, setTitle] = useState(detail.video.title)
  const [descriptionHtml, setDescriptionHtml] = useState(detail.video.descriptionHtml ?? '')
  const [shortDescription, setShortDescription] = useState(detail.video.shortDescription ?? '')
  const [access, setAccess] = useState<VideotecaAccess>(detail.video.access)
  const [urlSlug, setUrlSlug] = useState(detail.video.urlSlug)
  const [durationSeconds, setDurationSeconds] = useState(String(detail.video.durationSeconds ?? ''))
  const [keywords, setKeywords] = useState(detail.video.searchKeywords)
  const [visibilityState, setVisibilityState] = useState<'unpublished' | 'published' | 'scheduled'>(
    getVisibilityState(detail.video.visibleFromAt)
  )
  const [scheduledValue, setScheduledValue] = useState(
    toLocalDateTimeInputValue(detail.video.visibleFromAt)
  )

  const handleUploadCover = async (file: File) => {
    await onUploadCover(file)
  }

  const handleRemoveCover = async () => {
    await onRemoveCover()
  }

  const handleUploadVideo = async (file: File) => {
    await onUploadVideo(file)
  }

  const handleRemoveVideoFile = async () => {
    await onRemoveVideoFile()
  }

  return (
    <>
      <PanelSection title="Параметры видео">
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="URL slug">
            <Input value={urlSlug} onChange={(event) => setUrlSlug(event.target.value)} />
          </Field>
          <Field label="Description" className="lg:col-span-2">
            <HtmlEditor value={descriptionHtml} onChange={setDescriptionHtml} />
          </Field>
          <Field label="Short description" className="lg:col-span-2">
            <Textarea
              value={shortDescription}
              onChange={(event) => setShortDescription(event.target.value)}
            />
          </Field>
          <Field label="Access">
            <Select
              value={access}
              onChange={(event) => setAccess(event.target.value as VideotecaAccess)}
            >
              <option value="gated">gated</option>
              <option value="free_for_all">free_for_all</option>
            </Select>
          </Field>
          <Field label="Visibility">
            <VisibilityEditor
              currentVisibleFromAt={detail.video.visibleFromAt}
              visibilityState={visibilityState}
              setVisibilityState={setVisibilityState}
              scheduledValue={scheduledValue}
              setScheduledValue={setScheduledValue}
            />
          </Field>
          <Field label="Duration seconds">
            <Input
              type="number"
              value={durationSeconds}
              onChange={(event) => setDurationSeconds(event.target.value)}
            />
          </Field>
          <Field label="File size">
            <p style={bodyStyle}>{formatFileSize(detail.video.fileSizeBytes)}</p>
          </Field>
        </div>

        <Field label="Search keywords">
          <KeywordsEditor keywords={keywords} onChange={setKeywords} />
        </Field>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondaryAssist" onClick={() => void onDelete()}>
            Удалить видео
          </Button>
          <Button
            type="button"
            onClick={() =>
              void onSave({
                title: title.trim(),
                descriptionHtml: descriptionHtml.trim() || null,
                shortDescription: shortDescription.trim() || null,
                visibleFromAt: toVisibilityPayload(
                  visibilityState,
                  detail.video.visibleFromAt,
                  scheduledValue
                ),
                access,
                durationSeconds: durationSeconds.trim() ? Number(durationSeconds) : null,
                urlSlug: urlSlug.trim(),
                searchKeywords: keywords,
              })
            }
          >
            Сохранить видео
          </Button>
        </div>
      </PanelSection>

      <PanelSection
        title="Cover"
        description="Обложка видео загружается, заменяется и удаляется сразу, без участия основной кнопки сохранения."
      >
        <ManagedFileField
          label="Cover"
          fileLabel={detail.video.coverKey || detail.video.coverUrl}
          accept="image/jpeg,image/png,image/webp"
          hint={formatCatalogCoverValidationHint()}
          showInlineFeedback
          uploadSuccessMessage="Обложка видео загружена"
          removeSuccessMessage="Обложка видео удалена"
          onUpload={handleUploadCover}
          onRemove={handleRemoveCover}
        />
        {normalizedVideoCoverUrl ? (
          <img
            src={normalizedVideoCoverUrl}
            alt={detail.video.title}
            className="max-h-56 rounded-2xl object-cover"
          />
        ) : null}
      </PanelSection>

      <PanelSection
        title="Video file"
        description="Видео-файл загружается, заменяется и удаляется сразу, отдельно от кнопки сохранения параметров."
      >
        <ManagedFileField
          label="Video file"
          fileLabel={detail.video.videoKey || detail.video.videoUrl}
          showInlineFeedback
          uploadSuccessMessage="Видео-файл загружен"
          removeSuccessMessage="Видео-файл удалён"
          onUpload={handleUploadVideo}
          onRemove={handleRemoveVideoFile}
        />
      </PanelSection>

      <PanelSection title="Bundles">
        {detail.bundles.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>position: {item.position}</span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveBundle(item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        <ReferenceBrowser
          kind="bundle"
          actionLabel="Добавить bundle"
          linkedItems={detail.bundles.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddBundle}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveBundle(linkId) : Promise.resolve())}
        />
      </PanelSection>

      <PanelSection title="Категории">
        {detail.categories.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.urlSlug} · position: {item.position}
              </span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveCategory(item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        <ReferenceBrowser
          kind="category"
          actionLabel="Добавить категорию"
          linkedItems={detail.categories.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddCategory}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveCategory(linkId) : Promise.resolve())}
        />
      </PanelSection>

      <PanelSection title="Custom filters">
        <FilterGroupsEditor
          groups={detail.filterGroups}
          onSelectOption={onAddFilterOption}
          onClearSelection={onRemoveFilterOption}
        />
      </PanelSection>

      <PanelSection title="Resources">
        {detail.resources.map((item, index) => (
          <div
            key={item.linkId}
            className="flex flex-col gap-3 rounded-2xl border p-4 lg:flex-row lg:items-center lg:justify-between"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.fileName} · {item.mimeType ?? 'mime?'} · position: {item.position}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() => void onMoveResource(item.linkId, item.position - 1)}
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.resources.length - 1}
                onClick={() => void onMoveResource(item.linkId, item.position + 1)}
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onRemoveResource(item.linkId)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}
        <ReferenceBrowser
          kind="resource"
          actionLabel="Добавить resource"
          linkedItems={detail.resources.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddResource}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveResource(linkId) : Promise.resolve())}
          searchPlaceholder="Поиск по title"
        />
      </PanelSection>

      <PanelSection title="Collections">
        {detail.collections.map((item, index) => (
          <div
            key={item.linkId}
            className="flex flex-col gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                {item.urlSlug} · position: {item.position} · {item.isFree ? 'free' : 'gated'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() => void onMoveCollection(item.linkId, item.position - 1)}
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.collections.length - 1}
                onClick={() => void onMoveCollection(item.linkId, item.position + 1)}
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() =>
                  void onToggleCollectionFree(item.linkId, !item.isFree, item.position)
                }
              >
                {item.isFree ? 'Сделать gated' : 'Сделать free'}
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onRemoveCollection(item.linkId)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}
        <ReferenceBrowser
          kind="collection"
          actionLabel="Добавить collection"
          linkedItems={detail.collections.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={onAddCollection}
          onRemove={(_itemId, linkId) => (linkId ? onRemoveCollection(linkId) : Promise.resolve())}
        />
      </PanelSection>
    </>
  )
}

export function VideosTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'videos', page, appliedSearch],
    queryFn: () => loadAdminVideosList({ page, limit: pageSize, search: appliedSearch }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)
  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'video-detail', selectedId],
    queryFn: () => loadAdminVideoDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'videos'] })
    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'video-detail', selectedId],
      })
    }
  }

  const createVideoHandler = async () => {
    if (!createTitle.trim() || !createSlug.trim()) {
      return
    }

    try {
      const response = await createAdminVideo({
        title: createTitle.trim(),
        urlSlug: createSlug.trim(),
      })
      setCreateTitle('')
      setCreateSlug('')
      setRequestedId(response.detail.video.id)
      setFeedback('Видео создано')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveVideo = async (input: Parameters<typeof updateAdminVideo>[1]) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminVideo(selectedId, input)
      setFeedback('Видео сохранено')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const uploadCover = async (file: File) => {
    if (!selectedId) {
      throw new Error('Video is not selected')
    }

    let upload: UploadedManagedFile | null = null

    try {
      await validateCatalogCoverImage(file)
      const previousKey = detailQuery.data?.video.coverKey
      upload = await uploadManagedFile({ kind: 'cover', file, entityId: selectedId })
      await updateAdminVideo(selectedId, {
        coverBucket: upload.bucket,
        coverKey: upload.key,
        coverUrl: upload.browserUrl,
      })
      if (previousKey && previousKey !== upload.key) {
        await deleteManagedFileIfPresent(previousKey, 'cover')
      }
      await refresh()
      return upload
    } catch (error) {
      if (upload?.key) {
        await deleteManagedFileIfPresent(upload.key, 'cover').catch(() => undefined)
      }
      throw error
    }
  }

  const removeCover = async () => {
    if (!selectedId || !detailQuery.data) {
      return
    }

    if (!window.confirm('Удалить обложку видео?')) {
      return
    }

    const oldKey = detailQuery.data.video.coverKey
    await updateAdminVideo(selectedId, {
      coverBucket: null,
      coverKey: null,
      coverUrl: null,
    })
    await deleteManagedFileIfPresent(oldKey, 'cover')
    await refresh()
  }

  const uploadVideoFile = async (file: File) => {
    if (!selectedId) {
      throw new Error('Video is not selected')
    }

    let upload: UploadedManagedFile | null = null

    try {
      const previousKey = detailQuery.data?.video.videoKey
      upload = await uploadManagedFile({ kind: 'video', file, entityId: selectedId })
      await updateAdminVideo(selectedId, {
        videoBucket: upload.bucket,
        videoKey: upload.key,
        videoUrl: upload.browserUrl,
        fileSizeBytes: upload.sizeBytes,
      })
      if (previousKey && previousKey !== upload.key) {
        await deleteManagedFileIfPresent(previousKey, 'video')
      }
      await refresh()
      return upload
    } catch (error) {
      if (upload?.key) {
        await deleteManagedFileIfPresent(upload.key, 'video').catch(() => undefined)
      }
      throw error
    }
  }

  const removeVideoFile = async () => {
    if (!selectedId || !detailQuery.data) {
      return
    }

    if (!window.confirm('Удалить видео-файл?')) {
      return
    }

    const oldKey = detailQuery.data.video.videoKey
    await updateAdminVideo(selectedId, {
      videoBucket: null,
      videoKey: null,
      videoUrl: null,
      fileSizeBytes: null,
    })
    await deleteManagedFileIfPresent(oldKey, 'video')
    await refresh()
  }

  const deleteVideoHandler = async () => {
    if (!selectedId || !window.confirm('Удалить видео?')) {
      return
    }

    try {
      await deleteAdminVideo(selectedId)
      setRequestedId(null)
      setFeedback('Видео удалено')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const simpleAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action()
      setFeedback(successMessage)
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection
        title="Videos"
        description="Видео-карточки, файл воспроизведения и обратные связи с collections."
      >
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title / slug"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новое видео</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Title"
          />
          <Input
            value={createSlug}
            onChange={(event) => setCreateSlug(event.target.value)}
            placeholder="URL slug"
          />
          <Button type="button" onClick={() => void createVideoHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
            coverUrl={item.coverUrl}
            coverAlt={item.title}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>{item.urlSlug}</span>
              <span style={mutedStyle}>
                collections: {item.collectionCount} · resources: {item.resourceCount} ·{' '}
                {formatDuration(item.durationSeconds)}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}
        {detailQuery.data ? (
          <VideoEditor
            key={detailQuery.data.video.id}
            detail={detailQuery.data}
            onSave={saveVideo}
            onDelete={deleteVideoHandler}
            onUploadCover={uploadCover}
            onRemoveCover={removeCover}
            onUploadVideo={uploadVideoFile}
            onRemoveVideoFile={removeVideoFile}
            onAddBundle={(bundleId) =>
              simpleAction(
                () => addAdminVideoBundle({ id: selectedId as string, bundleId }),
                'Связь с bundle сохранена'
              )
            }
            onRemoveBundle={(linkId) =>
              window.confirm('Удалить связь с bundle?')
                ? simpleAction(
                    () => deleteAdminVideoBundle({ id: selectedId as string, linkId }),
                    'Связь с bundle удалена'
                  )
                : Promise.resolve()
            }
            onAddCategory={(categoryId) =>
              simpleAction(
                () => addAdminVideoCategory({ id: selectedId as string, categoryId }),
                'Категория добавлена'
              )
            }
            onRemoveCategory={(linkId) =>
              window.confirm('Удалить связь с категорией?')
                ? simpleAction(
                    () => deleteAdminVideoCategory({ id: selectedId as string, linkId }),
                    'Категория удалена'
                  )
                : Promise.resolve()
            }
            onAddFilterOption={(optionId) =>
              simpleAction(
                () => addAdminVideoFilterOption({ id: selectedId as string, optionId }),
                'Фильтр сохранён'
              )
            }
            onRemoveFilterOption={(linkId) =>
              window.confirm('Удалить выбранную filter option?')
                ? simpleAction(
                    () => deleteAdminVideoFilterOption({ id: selectedId as string, linkId }),
                    'Фильтр удалён'
                  )
                : Promise.resolve()
            }
            onAddResource={(resourceId) =>
              simpleAction(
                () => addAdminVideoResource({ id: selectedId as string, resourceId }),
                'Ресурс добавлен'
              )
            }
            onMoveResource={(linkId, position) =>
              simpleAction(
                () =>
                  updateAdminVideoResourcePosition({ id: selectedId as string, linkId, position }),
                'Порядок ресурсов обновлён'
              )
            }
            onRemoveResource={(linkId) =>
              window.confirm('Удалить ресурс из видео?')
                ? simpleAction(
                    () => deleteAdminVideoResource({ id: selectedId as string, linkId }),
                    'Ресурс удалён из видео'
                  )
                : Promise.resolve()
            }
            onAddCollection={(collectionId) =>
              simpleAction(
                () => addAdminVideoCollection({ id: selectedId as string, collectionId }),
                'Коллекция добавлена'
              )
            }
            onMoveCollection={(linkId, position) =>
              simpleAction(
                () =>
                  updateAdminVideoCollectionLink({ id: selectedId as string, linkId, position }),
                'Порядок collections обновлён'
              )
            }
            onToggleCollectionFree={(linkId, isFree, position) =>
              simpleAction(
                () =>
                  updateAdminVideoCollectionLink({
                    id: selectedId as string,
                    linkId,
                    isFree,
                    position,
                  }),
                'Флаг free обновлён'
              )
            }
            onRemoveCollection={(linkId) =>
              window.confirm('Удалить collection из видео?')
                ? simpleAction(
                    () => deleteAdminVideoCollection({ id: selectedId as string, linkId }),
                    'Коллекция удалена из видео'
                  )
                : Promise.resolve()
            }
          />
        ) : (
          <PanelSection title="Параметры видео">
            <p style={mutedStyle}>Выберите видео слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}
